import { Alert, Button, Form, InputNumber, Modal, Table, Tag, message } from "antd";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../../redux/slices/auth.slice";
import {
  createSmsWalletRechargeOrder,
  fetchSmsWalletBalance,
  fetchSmsWalletStatement,
  smsWalletError,
  verifySmsWalletRechargePayment,
} from "../../../../../services/sms-wallet.service";

const WALLET_LEDGER_COLOR = {
  RECHARGE: "green",
  DEBIT: "red",
  REFUND: "blue",
  ADJUSTMENT: "orange",
};

/** Same loader used by the WhatsApp wallet's recharge modal. */
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

/**
 * Prepaid SMS wallet for fee reminders and fee-payment-confirmation SMS.
 * A separate balance from the WhatsApp wallet — each fee SMS costs ₹0.20
 * and is skipped (not queued) if the balance can't cover it.
 */
const SmsWalletSettings = () => {
  const queryClient = useQueryClient();
  const user = useSelector(selectUser);

  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rechargeForm] = Form.useForm();
  const [recharging, setRecharging] = useState(false);
  const [statementPage, setStatementPage] = useState(1);

  const wallet = useQuery("sms-wallet/balance", fetchSmsWalletBalance);
  const walletBalancePaise = wallet?.data?.balance_paise ?? 0;

  const statement = useQuery(
    ["sms-wallet/statement", statementPage],
    () => fetchSmsWalletStatement({ page: statementPage, limit: 10 }),
  );

  const handleRecharge = async (values) => {
    setRecharging(true);
    try {
      const amountPaise = Math.round(Number(values.amount) * 100);
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        message.error("Razorpay SDK failed to load. Are you online?");
        setRecharging(false);
        return;
      }

      const schoolId = user?.school?._id || user?.school;
      const order = await createSmsWalletRechargeOrder(amountPaise, schoolId);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: "is-kool SMS Wallet",
        description: "SMS wallet recharge",
        order_id: order.id,
        handler: async (response) => {
          try {
            await verifySmsWalletRechargePayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            message.success("Wallet recharged successfully");
            setRechargeOpen(false);
            rechargeForm.resetFields();
            queryClient.invalidateQueries("sms-wallet/balance");
            queryClient.invalidateQueries("sms-wallet/statement");
          } catch (err) {
            message.error(
              smsWalletError(err, "Payment verification failed. Please contact support."),
            );
          } finally {
            setRecharging(false);
          }
        },
        modal: {
          ondismiss: () => setRecharging(false),
        },
        theme: { color: "#1a237e" },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      message.error(smsWalletError(err, "Could not start the recharge"));
      setRecharging(false);
    }
  };

  return (
    <div className="mt-8">
      <div
        style={{ borderRadius: "10px", color: "#616161" }}
        className="bg-white drop-shadow-md p-8 w-full"
      >
        <div className="text-2xl text-orange-500 font-semibold">SMS Wallet</div>
        <div className="text-sm text-gray-500 mt-1">
          Every fee reminder and fee-payment-confirmation SMS costs ₹0.20.
          Recharge below to top up your school's balance. Other SMS (OTP,
          absent alerts, etc.) are not billed against this wallet.
        </div>

        <div className="flex items-center justify-between pt-6">
          <div>
            <div className="text-gray-400 text-sm">Current balance</div>
            <div
              className="inline-block mt-1 px-4 py-2 rounded-lg text-3xl font-bold"
              style={{
                color: walletBalancePaise > 0 ? "#1a237e" : "#cf1322",
                backgroundColor: walletBalancePaise > 0 ? "#E8EAF6" : "#fff1f0",
              }}
            >
              {wallet?.isLoading
                ? "…"
                : `₹${(walletBalancePaise / 100).toFixed(2)}`}
            </div>
          </div>
          <Button
            size="large"
            type="primary"
            onClick={() => setRechargeOpen(true)}
          >
            Recharge
          </Button>
        </div>

        <div className="mt-8">
          <div className="text-sm text-gray-500 mb-2">Statement</div>
          <Table
            size="small"
            rowKey="_id"
            loading={statement?.isLoading}
            dataSource={statement?.data?.data || []}
            pagination={{
              current: statementPage,
              pageSize: 10,
              total: statement?.data?.total || 0,
              onChange: (page) => setStatementPage(page),
            }}
            columns={[
              {
                title: "Type",
                dataIndex: "type",
                render: (type) => (
                  <Tag color={WALLET_LEDGER_COLOR[type] || "default"}>{type}</Tag>
                ),
              },
              {
                title: "Amount",
                dataIndex: "amount_paise",
                render: (paise) => `₹${(paise / 100).toFixed(2)}`,
              },
              {
                title: "Balance after",
                dataIndex: "balance_after_paise",
                render: (paise) => `₹${(paise / 100).toFixed(2)}`,
              },
              {
                title: "Date",
                dataIndex: "createdAt",
                render: (d) => (d ? new Date(d).toLocaleString() : "—"),
              },
              { title: "Note", dataIndex: "note" },
            ]}
          />
        </div>
      </div>

      <Modal
        title="Recharge SMS Wallet"
        open={rechargeOpen}
        onCancel={() => {
          setRechargeOpen(false);
          rechargeForm.resetFields();
        }}
        footer={null}
      >
        <Form form={rechargeForm} layout="vertical" onFinish={handleRecharge}>
          <Alert
            className="mb-4"
            type="info"
            showIcon
            message="You are about to make a secure online payment via Razorpay."
          />
          <Form.Item
            label="Amount (₹)"
            name="amount"
            rules={[
              { required: true, message: "Enter an amount" },
              {
                type: "number",
                min: 1,
                message: "Amount must be at least ₹1",
              },
            ]}
          >
            <InputNumber className="w-full" min={1} placeholder="e.g. 500" />
          </Form.Item>
          <Button
            className="w-full"
            size="large"
            type="primary"
            htmlType="submit"
            loading={recharging}
          >
            Pay & Recharge
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default SmsWalletSettings;
